import React, { Component } from 'react';
import { Row, Col, Card, CardBody, Input, Button } from 'reactstrap';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { geoMercator, geoPath } from 'd3-geo';
import { feature as topojsonFeature } from 'topojson-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HistoryControls from './shared/HistoryControls';
import { getVisibleYears } from './shared/historyUtils';
import {
  faSpinner,
  faSignal,
  faChartLine,
  faMapMarkerAlt,
  faArrowLeft,
  faMedal,
  faEquals,
  faProjectDiagram
} from '@fortawesome/free-solid-svg-icons';

const HEATMAP_COLORS = ['#1e3a8a', '#2563eb', '#60a5fa', '#cbd5e1', '#fdba74', '#f97316', '#dc2626'];
const MAP_MODES = [
  { key: 'value', label: 'Value' },
  { key: 'rank', label: 'Rank' },
  { key: 'vs_avg', label: 'Vs Avg' },
  { key: 'growth', label: 'Growth' }
];

const DISPLAY_MODES = [
  { key: 'bubble', label: 'Bubble Map' },
  { key: 'filled', label: 'Filled Map' }
];

const TOP_LABEL_MODES = [
  { key: 'tooltip', label: 'Tooltips' },
  { key: 'labels', label: 'Labels' }
];

class HeatmapExplorer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedMetric: null,
      selectedYear: '',
      selectedMode: 'value',
      displayMode: 'bubble',
      bubbleOpacity: 72,
      topLabelMode: 'tooltip',
      bubbleTooltip: null,
      historyWindow: 'recent',
      heatmapData: null,
      populationHeatmapData: null,
      featureCollection: null,
      geographies: [],
      hoveredFeatureId: null,
      selectedFeatureId: null,
      loading: false,
      mapWidth: 860,
      mapHeight: 560
    };

    this.containerRef = React.createRef();
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    this.ensureDefaultMetric();
    this.setupResizeTracking();
    this.loadGeography();
  }

  componentDidUpdate(prevProps, prevState) {
    if(prevProps.dataPointNames !== this.props.dataPointNames && !this.state.selectedMetric) {
      this.ensureDefaultMetric();
    }

    if(prevProps.province !== this.props.province || prevProps.prefecture !== this.props.prefecture) {
      this.setState({
        selectedYear: '',
        displayMode: 'bubble',
        bubbleTooltip: null,
        heatmapData: null,
        populationHeatmapData: null,
        hoveredFeatureId: null,
        selectedFeatureId: null,
        historyWindow: 'recent'
      }, () => {
        this.loadGeography();
        this.loadHeatmap();
      });
    }

    if(prevState.selectedMetric !== this.state.selectedMetric) {
      this.setState({ selectedYear: '', selectedMode: 'value', displayMode: 'bubble', bubbleTooltip: null, historyWindow: 'recent' }, () => this.loadHeatmap());
    }

    if(prevState.selectedYear !== this.state.selectedYear && prevState.selectedMetric === this.state.selectedMetric) {
      this.loadHeatmap();
    }
  }

  componentWillUnmount() {
    if(this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleResize);
  }

  setupResizeTracking() {
    this.handleResize();

    if(typeof ResizeObserver !== 'undefined' && this.containerRef.current) {
      this.resizeObserver = new ResizeObserver(() => this.handleResize());
      this.resizeObserver.observe(this.containerRef.current);
    } else {
      window.addEventListener('resize', this.handleResize);
    }
  }

  handleResize() {
    const node = this.containerRef.current;
    if(!node) return;

    const rect = node.getBoundingClientRect();
    const nextWidth = Math.max(360, Math.round(rect.width || node.clientWidth || 860));
    const nextHeight = Math.max(400, Math.round((rect.height || node.clientHeight || nextWidth * 0.64)));

    if(nextWidth !== this.state.mapWidth || nextHeight !== this.state.mapHeight) {
      this.setState({ mapWidth: nextWidth, mapHeight: nextHeight });
    }
  }

  ensureDefaultMetric() {
    const metricOptions = this.getMetricOptions();
    if(metricOptions.length > 0 && !this.state.selectedMetric) {
      this.setState({ selectedMetric: metricOptions[0].name });
    }
  }

  getMetricOptions() {
    return (this.props.dataPointNames || []).filter((metric) => metric.category !== 'General');
  }

  getScopeConfig() {
    const { province, prefecture } = this.props;

    if(prefecture && province) {
      return {
        level: 'district',
        title: prefecture.attributes.name + ' district heatmap',
        subtitle: province.attributes.name + ' Province · District boundary view',
        geographyPath: '/geographies/prefectures/' + province.attributes.name + '/' + prefecture.attributes.name + '.json',
        parentId: prefecture.id,
        clickable: false
      };
    }

    if(province) {
      return {
        level: 'prefecture',
        title: province.attributes.name + ' prefecture heatmap',
        subtitle: 'Prefecture boundary view',
        geographyPath: '/geographies/provinces/' + province.attributes.name + '.json',
        parentId: province.id,
        clickable: true
      };
    }

    return {
      level: 'province',
      title: 'China province heatmap',
      subtitle: 'Province boundary view',
      geographyPath: '/geographies/china.json',
      parentId: null,
      clickable: true
    };
  }

  loadGeography() {
    const scope = this.getScopeConfig();

    fetch(scope.geographyPath).then((resp) => resp.json())
      .then((topology) => {
        const objectName = Object.keys(topology.objects)[0];
        const featureCollection = topojsonFeature(topology, topology.objects[objectName]);

        this.setState({
          featureCollection,
          geographies: featureCollection.features
        }, () => {
          window.requestAnimationFrame(this.handleResize);
        });
      });
  }

  loadHeatmap() {
    const scope = this.getScopeConfig();
    const { selectedMetric, selectedYear, selectedFeatureId } = this.state;

    if(!selectedMetric) return;

    const buildParams = (metricName) => {
      const params = new URLSearchParams({
        geography_level: scope.level,
        data_point_name: metricName
      });

      if(scope.parentId) {
        params.set('parent_id', scope.parentId);
      }

      if(selectedYear) {
        params.set('year', selectedYear);
      }

      return params;
    };

    const metricRequest = fetch('/api/data_exploration/getBoundaryHeatmap?' + buildParams(selectedMetric).toString()).then((resp) => resp.json());
    const populationRequest = selectedMetric === 'Total Population'
      ? Promise.resolve(null)
      : fetch('/api/data_exploration/getBoundaryHeatmap?' + buildParams('Total Population').toString()).then((resp) => resp.json());

    this.setState({ loading: true });
    Promise.all([metricRequest, populationRequest])
      .then(([respJson, populationResp]) => {
        const hasSelected = (respJson.features || []).some((item) => item.id === selectedFeatureId);
        this.setState({
          heatmapData: respJson,
          populationHeatmapData: selectedMetric === 'Total Population' ? respJson : populationResp,
          selectedYear: respJson.selected_year ? String(respJson.selected_year) : '',
          selectedFeatureId: hasSelected ? selectedFeatureId : (respJson.features && respJson.features[0] ? respJson.features[0].id : null),
          loading: false
        });
      })
      .catch(() => {
        this.setState({ heatmapData: null, populationHeatmapData: null, loading: false });
      });
  }

  buildProjection(width, height) {
    const { featureCollection } = this.state;
    const projection = geoMercator();

    if(featureCollection && featureCollection.features && featureCollection.features.length > 0) {
      const inset = Math.max(18, Math.round(Math.min(width, height) * 0.045));
      projection.fitExtent([[inset, inset], [width - inset, height - inset]], featureCollection);
    }

    return projection;
  }

  normalizeMapKey(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .replace(/\([^)]*\)/g, '')
      .replace(/\b(city|county|district|area|newarea|newdistrict|autonomouscounty|autonomousprefecture|prefecture)\b/g, '')
      .replace(/[^a-z0-9\u4e00-\u9fff]/g, '');
  }

  expandAliases(value) {
    if(!value) return [];

    const raw = value.toString().trim();
    const variants = new Set([raw]);
    const withoutParen = raw.replace(/\([^)]*\)/g, '').trim();
    variants.add(withoutParen);
    variants.add(withoutParen.replace(/\s*\+\s*/g, ' '));
    variants.add(withoutParen.replace(/\s*City$/i, '').trim());
    variants.add(withoutParen.replace(/\s*County$/i, '').trim());
    variants.add(withoutParen.replace(/\s*District$/i, '').trim());
    variants.add(withoutParen.replace(/\s*Area$/i, '').trim());
    variants.add(withoutParen.replace(/\s+/g, ' ').trim());

    return Array.from(variants).filter(Boolean);
  }

  getFeatureAliases(feature) {
    return [feature.name, feature.chinese_name].flatMap((alias) => this.expandAliases(alias));
  }

  getGeoAliases(geoProps) {
    return [geoProps.name, geoProps.chineseName, geoProps.id].flatMap((alias) => this.expandAliases(alias));
  }

  getFeatureCandidates(alias, features) {
    const normalizedAlias = this.normalizeMapKey(alias);
    if(!normalizedAlias) return [];

    return features.filter((item) => this.getFeatureAliases(item).some((featureAlias) => this.normalizeMapKey(featureAlias) === normalizedAlias));
  }

  getFeatureForGeo(geoProps, featureList = null) {
    const aliases = typeof geoProps === 'string' ? [geoProps] : this.getGeoAliases(geoProps || {});
    const features = featureList || (this.state.heatmapData && this.state.heatmapData.features ? this.state.heatmapData.features : []);

    for(const alias of aliases) {
      const exact = features.find((item) => item.name === alias || item.chinese_name === alias || String(item.id) === String(alias));
      if(exact) return exact;
    }

    for(const alias of aliases) {
      const candidates = this.getFeatureCandidates(alias, features);
      if(candidates.length === 1) return candidates[0];
    }

    return null;
  }

  getFeatureById(featureId) {
    const features = this.state.heatmapData && this.state.heatmapData.features ? this.state.heatmapData.features : [];
    return features.find((item) => item.id === featureId) || null;
  }

  getActiveFeature() {
    const activeId = this.state.hoveredFeatureId || this.state.selectedFeatureId;
    return activeId ? this.getFeatureById(activeId) : null;
  }

  getPopulationFeatures() {
    return this.state.populationHeatmapData && this.state.populationHeatmapData.features
      ? this.state.populationHeatmapData.features
      : [];
  }

  getPopulationFeatureForGeo(geoProps) {
    return this.getFeatureForGeo(geoProps, this.getPopulationFeatures());
  }

  formatPopulationValue(value) {
    const unit = this.state.populationHeatmapData ? this.state.populationHeatmapData.unit : null;
    return this.formatLegendValue(value, 'value') + (unit && unit !== '%' ? ' ' + unit : unit === '%' ? '%' : '');
  }

  getTopBubbleIds() {
    return this.getPopulationFeatures()
      .filter((feature) => typeof feature.value === 'number' && feature.value > 0)
      .sort((left, right) => right.value - left.value)
      .slice(0, 5)
      .map((feature) => feature.id);
  }

  getBubbleLegendItems() {
    const values = this.getPopulationFeatures().map((item) => item.value).filter((value) => typeof value === 'number' && value > 0).sort((a, b) => a - b);
    if(values.length === 0) return [];

    const pick = (ratio) => values[Math.min(values.length - 1, Math.max(0, Math.round((values.length - 1) * ratio)))];
    const candidates = [
      { label: 'Large', value: values[values.length - 1] },
      { label: 'Medium', value: pick(0.6) },
      { label: 'Small', value: pick(0.25) }
    ];

    return candidates.filter((item, index, collection) => collection.findIndex((candidate) => candidate.value === item.value) === index);
  }

  getBubbleRadius(feature, areaCap = null) {
    const values = this.getPopulationFeatures().map((item) => item.value).filter((value) => typeof value === 'number' && value > 0);
    if(!feature || values.length === 0) return 0;

    const maxValue = Math.max(...values);
    if(!maxValue) return 0;

    const minDimension = Math.min(this.state.mapWidth, this.state.mapHeight);
    const densityFactor = Math.max(0.6, Math.min(1.28, Math.sqrt(14 / Math.max(values.length, 1))));
    const maxRadius = Math.max(24, Math.min(78, minDimension * 0.118 * densityFactor));
    const minRadius = Math.max(9, maxRadius * 0.3);
    let radius = minRadius + (Math.sqrt(feature.value / maxValue) * (maxRadius - minRadius));

    if(areaCap && Number.isFinite(areaCap)) {
      radius = Math.min(radius, Math.max(12, areaCap * 0.92));
    }

    return radius;
  }

  getVisibleYearsForFeature(feature) {
    if(!feature || !feature.years) return [];
    return getVisibleYears(feature.years, this.state.historyWindow);
  }

  getWindowGrowth(feature) {
    const visibleYears = this.getVisibleYearsForFeature(feature);
    if(visibleYears.length < 2) return null;

    const first = feature.years[visibleYears[0]];
    const last = feature.years[visibleYears[visibleYears.length - 1]];
    if(first === null || first === undefined || last === null || last === undefined || first === 0) return null;

    return ((last - first) / Math.abs(first)) * 100;
  }

  formatGrowth(growth) {
    if(growth === null || growth === undefined || Number.isNaN(growth)) return 'No trend';
    const rounded = growth.toFixed(1);
    return (growth > 0 ? '+' : '') + rounded + '%';
  }

  getModeScore(feature, mode = this.state.selectedMode) {
    if(!feature) return null;

    switch(mode) {
    case 'rank': {
      const total = this.state.heatmapData && this.state.heatmapData.summary ? this.state.heatmapData.summary.count : 0;
      return total > 0 ? (total - feature.rank + 1) : null;
    }
    case 'vs_avg':
      return feature.delta_vs_mean;
    case 'growth':
      return this.getWindowGrowth(feature);
    case 'value':
    default:
      return feature.value;
    }
  }

  getModeLabel(mode = this.state.selectedMode) {
    const config = MAP_MODES.find((item) => item.key === mode);
    return config ? config.label : 'Value';
  }

  getModeFormattedValue(feature, mode = this.state.selectedMode) {
    if(!feature) return '—';

    switch(mode) {
    case 'rank':
      return '#' + feature.rank;
    case 'vs_avg':
      return feature.delta_vs_mean_formatted || '—';
    case 'growth':
      return this.formatGrowth(this.getWindowGrowth(feature));
    case 'value':
    default:
      return feature.formatted_value;
    }
  }

  getModeSummaryCopy() {
    switch(this.state.selectedMode) {
    case 'rank':
      return 'Coloring regions by standing within the current scope.';
    case 'vs_avg':
      return 'Coloring regions by difference versus the current scope average.';
    case 'growth':
      return 'Coloring regions by long-window growth where history exists.';
    case 'value':
    default:
      return 'Coloring regions by current value for the selected year.';
    }
  }

  getModeBreaks(mode = this.state.selectedMode) {
    const features = this.state.heatmapData && this.state.heatmapData.features ? this.state.heatmapData.features : [];
    const values = features
      .map((feature) => this.getModeScore(feature, mode))
      .filter((value) => typeof value === 'number' && !Number.isNaN(value))
      .sort((a, b) => a - b);

    if(values.length === 0) return [];

    return HEATMAP_COLORS.map((_, index) => {
      const position = values.length === 1 ? 0 : Math.round(((values.length - 1) * index) / (HEATMAP_COLORS.length - 1));
      return values[position];
    });
  }

  getModeBucket(value, breaks) {
    if(typeof value !== 'number' || Number.isNaN(value) || breaks.length === 0) {
      return -1;
    }

    for(let index = 0; index < breaks.length; index += 1) {
      if(value <= breaks[index]) return index;
    }

    return breaks.length - 1;
  }

  formatLegendValue(value, mode = this.state.selectedMode) {
    if(typeof value !== 'number' || Number.isNaN(value)) return '—';

    if(mode === 'growth') {
      return value.toFixed(1) + '%';
    }

    if(mode === 'rank') {
      return '#' + Math.round(value).toLocaleString();
    }

    if(mode === 'vs_avg') {
      const unit = this.state.heatmapData ? this.state.heatmapData.unit : null;
      const sign = value > 0 ? '+' : '';
      if(unit === '%') return sign + value.toFixed(1) + '%';
      return sign + value.toLocaleString();
    }

    return value.toLocaleString();
  }

  hexToRgba(hex, alpha) {
    const normalized = (hex || '').replace('#', '');
    if(normalized.length !== 6) return 'rgba(148, 163, 184, ' + alpha + ')';

    const bigint = parseInt(normalized, 16);
    const red = (bigint >> 16) & 255;
    const green = (bigint >> 8) & 255;
    const blue = bigint & 255;
    return 'rgba(' + red + ', ' + green + ', ' + blue + ', ' + alpha + ')';
  }

  getBubbleColor(feature) {
    if(!feature) return '#94a3b8';

    const breaks = this.getModeBreaks();
    const score = this.getModeScore(feature);
    const bucket = this.getModeBucket(score, breaks);
    if(bucket < 0) return '#94a3b8';

    return HEATMAP_COLORS[Math.min(bucket, HEATMAP_COLORS.length - 1)];
  }

  getBaseMapFill() {
    if(typeof document !== 'undefined' && document.body.classList.contains('dark-mode')) {
      return '#182538';
    }
    return '#edf2f7';
  }

  getSortedFeatures(mode = this.state.selectedMode) {
    const features = this.state.heatmapData && this.state.heatmapData.features ? this.state.heatmapData.features : [];

    return features
      .filter((feature) => typeof this.getModeScore(feature, mode) === 'number' && !Number.isNaN(this.getModeScore(feature, mode)))
      .sort((left, right) => this.getModeScore(right, mode) - this.getModeScore(left, mode));
  }

  getSparklineGeometry(feature) {
    const years = this.getVisibleYearsForFeature(feature).map((year) => parseInt(year, 10));
    if(years.length === 0) return null;

    const values = years.map((year) => feature.years[year]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const width = 180;
    const height = 48;
    const points = values.map((value, index) => {
      const x = years.length === 1 ? width / 2 : (index / (years.length - 1)) * width;
      const ratio = max === min ? 0.5 : (value - min) / (max - min);
      const y = height - (ratio * (height - 8)) - 4;
      return {
        x: parseFloat(x.toFixed(1)),
        y: parseFloat(y.toFixed(1))
      };
    });

    return {
      years,
      points,
      polyline: points.map((point) => point.x + ',' + point.y).join(' ')
    };
  }

  handleGeoClick(geoProps) {
    const scope = this.getScopeConfig();
    const match = this.getFeatureForGeo(geoProps);
    this.setState({ selectedFeatureId: match ? match.id : null });

    if(!scope.clickable) return;

    if(scope.level === 'province') {
      fetch('/api/provinces?filter[name]=' + encodeURIComponent(geoProps.name)).then((resp) => resp.json())
        .then((respJson) => {
          if(respJson.data && respJson.data[0]) {
            this.props.updatePrefectures({ province: respJson.data[0], prefecture: null });
          }
        });
      return;
    }

    if(scope.level === 'prefecture') {
      fetch('/api/prefectures?filter[name]=' + encodeURIComponent(geoProps.name)).then((resp) => resp.json())
        .then((respJson) => {
          (respJson.data || []).forEach((item) => {
            fetch(item.relationships.province.links.related).then((resp) => resp.json())
              .then((provinceResp) => {
                if(this.props.province && provinceResp.data.attributes.name === this.props.province.attributes.name) {
                  this.props.updatePrefectures({ prefecture: item });
                }
              });
          });
        });
    }
  }

  handleBackScope() {
    if(this.props.prefecture) {
      this.props.updatePrefectures({ prefecture: null });
      return;
    }

    if(this.props.province) {
      this.props.updatePrefectures({ province: null, prefecture: null });
    }
  }

  getTrendSummary(feature) {
    const delta = this.getWindowGrowth(feature);
    if(delta === null || delta === undefined || Number.isNaN(delta)) return null;

    return {
      label: Math.abs(delta).toFixed(1) + '% ' + (delta >= 0 ? 'up' : 'down'),
      direction: delta >= 0 ? 'up' : 'down'
    };
  }

  renderInlineReadout() {
    const activeFeature = this.getActiveFeature();

    if(!activeFeature) {
      return (
        <div className="heatmap-inline-readout is-empty">
          Hover over a region to inspect its current metric, rank, and change.
        </div>
      );
    }

    return (
      <div className="heatmap-inline-readout">
        <div className="heatmap-inline-readout-name">{activeFeature.name}</div>
        <div className="heatmap-inline-readout-value">{this.getModeFormattedValue(activeFeature)}</div>
        <div className="heatmap-inline-readout-sub">{this.getModeLabel()} view</div>
      </div>
    );
  }

  renderLegendInline() {
    const breaks = this.getModeBreaks();
    if(breaks.length === 0) return null;

    return (
      <div className="heatmap-inline-legend">
        {HEATMAP_COLORS.map((color, index) => {
          const lower = index === 0 ? breaks[0] : breaks[index - 1];
          const upper = breaks[index];
          return (
            <div className="heatmap-inline-legend-stop" key={color + '-' + index}>
              <span className="heatmap-inline-legend-swatch" style={{ backgroundColor: color }} />
              <span className="heatmap-inline-legend-label">
                {index === 0 ? this.formatLegendValue(upper) : this.formatLegendValue(lower) + ' - ' + this.formatLegendValue(upper)}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  renderMetricPicker() {
    const metricOptions = this.getMetricOptions();

    return (
      <div className="heatmap-toolbar-field heatmap-toolbar-metric">
        <label className="heatmap-label">Metric</label>
        <Input type="select" value={this.state.selectedMetric || ''} onChange={(event) => this.setState({ selectedMetric: event.target.value })}>
          {metricOptions.map((metric) => (
            <option key={metric.name} value={metric.name}>{metric.category} · {metric.title || metric.name}</option>
          ))}
        </Input>
      </div>
    );
  }

  renderModeToggle() {
    return (
      <div className="heatmap-toolbar-field heatmap-toolbar-mode">
        <label className="heatmap-label">Map mode</label>
        <div className="heatmap-mode-toggle">
          {MAP_MODES.map((mode) => (
            <button
              key={mode.key}
              type="button"
              aria-pressed={this.state.selectedMode === mode.key}
              className={'heatmap-mode-chip' + (this.state.selectedMode === mode.key ? ' is-active' : '')}
              onClick={() => this.setState({ selectedMode: mode.key, bubbleTooltip: null })}>
              {mode.label}
            </button>
          ))}
        </div>
      </div>
    );
  }


  renderDisplayToggle() {
    return (
      <div className="heatmap-toolbar-field heatmap-toolbar-display">
        <label className="heatmap-label">Display</label>
        <div className="heatmap-mode-toggle heatmap-display-toggle">
          {DISPLAY_MODES.map((mode) => (
            <button
              key={mode.key}
              type="button"
              aria-pressed={this.state.displayMode === mode.key}
              className={'heatmap-mode-chip' + (this.state.displayMode === mode.key ? ' is-active' : '')}
              onClick={() => this.setState({ displayMode: mode.key, bubbleTooltip: null })}>
              {mode.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  renderBubbleOpacityControl() {
    if(this.state.displayMode !== 'bubble') return null;

    return (
      <div className="heatmap-toolbar-field heatmap-toolbar-opacity">
        <label className="heatmap-label">Bubble opacity</label>
        <div className="heatmap-opacity-control">
          <input
            className="heatmap-opacity-slider"
            type="range"
            min="35"
            max="100"
            step="1"
            value={this.state.bubbleOpacity}
            onChange={(event) => this.setState({ bubbleOpacity: parseInt(event.target.value, 10) || 72 })} />
          <span className="heatmap-opacity-value">{this.state.bubbleOpacity}%</span>
        </div>
      </div>
    );
  }

  renderTopLabelToggle() {
    if(this.state.displayMode !== 'bubble') return null;

    return (
      <div className="heatmap-toolbar-field heatmap-toolbar-top-labels">
        <label className="heatmap-label">Top 5 names</label>
        <div className="heatmap-mode-toggle heatmap-display-toggle">
          {TOP_LABEL_MODES.map((mode) => (
            <button
              key={mode.key}
              type="button"
              aria-pressed={this.state.topLabelMode === mode.key}
              className={'heatmap-mode-chip' + (this.state.topLabelMode === mode.key ? ' is-active' : '')}
              onClick={() => this.setState({ topLabelMode: mode.key, bubbleTooltip: null })}>
              {mode.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  renderBubbleTooltip() {
    const { bubbleTooltip, displayMode, topLabelMode } = this.state;
    if(displayMode !== 'bubble' || topLabelMode !== 'tooltip' || !bubbleTooltip) return null;

    return (
      <div
        className="heatmap-bubble-tooltip"
        style={{
          left: bubbleTooltip.x,
          top: bubbleTooltip.y
        }}>
        <div className="heatmap-bubble-tooltip-name">{bubbleTooltip.name}</div>
        <div className="heatmap-bubble-tooltip-value">{bubbleTooltip.metricValue}</div>
        <div className="heatmap-bubble-tooltip-sub">Population {bubbleTooltip.populationValue}</div>
      </div>
    );
  }

  renderSparkline(feature) {
    const geometry = this.getSparklineGeometry(feature);
    if(!geometry) return null;

    return (
      <div className="heatmap-sparkline-block">
        <div className="heatmap-sparkline-head">
          <span>History profile</span>
          <span>{this.getVisibleYearsForFeature(feature).length} years</span>
        </div>
        <svg className="heatmap-sparkline" viewBox="0 0 180 48" preserveAspectRatio="none" aria-hidden="true">
          {geometry.points.length > 1 ? <polyline points={geometry.polyline} /> : null}
          {geometry.points.map((point, index) => (
            <circle key={feature.id + '-spark-' + index} cx={point.x} cy={point.y} r={4} />
          ))}
        </svg>
      </div>
    );
  }

  renderSelectionPanel() {
    const activeFeature = this.getActiveFeature();
    const trend = this.getTrendSummary(activeFeature);

    return (
      <Card className="heatmap-side-card heatmap-detail-card heatmap-selection-card">
        <CardBody>
          <div className="heatmap-side-title"><FontAwesomeIcon icon={faMapMarkerAlt} /> Region Readout</div>
          {activeFeature ? (
            <div className="heatmap-selection-panel">
              <div className="heatmap-selection-name">{activeFeature.name}</div>
              <div className="heatmap-selection-value">{activeFeature.formatted_value}</div>
              <div className="heatmap-chip-row">
                <div className="heatmap-chip-stat"><FontAwesomeIcon icon={faMedal} /> Rank #{activeFeature.rank}</div>
                <div className="heatmap-chip-stat"><FontAwesomeIcon icon={faSignal} /> {activeFeature.percentile}% percentile</div>
                <div className="heatmap-chip-stat"><FontAwesomeIcon icon={faEquals} /> {activeFeature.delta_vs_mean_formatted || '—'} vs avg</div>
                <div className="heatmap-chip-stat"><FontAwesomeIcon icon={faChartLine} /> {this.formatGrowth(this.getWindowGrowth(activeFeature))}</div>
              </div>
              <div className="heatmap-selection-grid heatmap-selection-grid-centered">
                <div className="heatmap-selection-stat">
                  <span className="heatmap-selection-label">Mode</span>
                  <strong>{this.getModeLabel()}</strong>
                </div>
                <div className="heatmap-selection-stat">
                  <span className="heatmap-selection-label">Mode value</span>
                  <strong>{this.getModeFormattedValue(activeFeature)}</strong>
                </div>
                <div className="heatmap-selection-stat">
                  <span className="heatmap-selection-label">Year</span>
                  <strong>{this.state.heatmapData ? this.state.heatmapData.selected_year : '—'}</strong>
                </div>
                <div className="heatmap-selection-stat">
                  <span className="heatmap-selection-label">History</span>
                  <strong>{this.getVisibleYearsForFeature(activeFeature).length} years</strong>
                </div>
              </div>
              {this.renderSparkline(activeFeature)}
              {trend ? (
                <div className={'heatmap-trend-pill ' + (trend.direction === 'up' ? 'is-up' : 'is-down')}>
                  <FontAwesomeIcon icon={faChartLine} /> {trend.label}
                </div>
              ) : (
                <div className="heatmap-summary-line">Single-year value available</div>
              )}
              <div className="heatmap-year-strip heatmap-year-strip-centered">
                {this.getVisibleYearsForFeature(activeFeature).slice(-6).map((year) => (
                  <div className="heatmap-year-item" key={activeFeature.name + '-' + year}>
                    <span className="heatmap-year-label">{year}</span>
                    <span className="heatmap-year-value">{activeFeature.years[year].toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="heatmap-summary-line">Hover over a region or select one from the ranking lists.</div>
          )}
        </CardBody>
      </Card>
    );
  }

  renderRankedList(title, items) {
    return (
      <Card className="heatmap-side-card heatmap-detail-card">
        <CardBody>
          <div className="heatmap-side-title">{title}</div>
          <div className="heatmap-ranked-list">
            {items.map((item, index) => (
              <button type="button" className="heatmap-ranked-item" key={title + '-' + item.name} onClick={() => this.setState({ selectedFeatureId: item.id })}>
                <span className="heatmap-ranked-index">{index + 1}</span>
                <div className="heatmap-ranked-copy">
                  <div className="heatmap-ranked-name">{item.name}</div>
                  <div className="heatmap-ranked-value">{this.getModeFormattedValue(item)}</div>
                  <div className="heatmap-ranked-sub">#{item.rank} · {item.percentile}% percentile</div>
                </div>
              </button>
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  render() {
    const { selectedYear, loading, geographies, mapWidth, mapHeight, heatmapData } = this.state;
    const scope = this.getScopeConfig();
    const summary = heatmapData && heatmapData.summary ? heatmapData.summary : null;
    const features = this.getSortedFeatures();
    const topItems = features.slice(0, 5);
    const bottomItems = features.slice().reverse().slice(0, 5).reverse();
    const mapRenderKey = [
      scope.level,
      this.state.selectedMetric || 'metric',
      selectedYear || 'year',
      this.state.selectedMode,
      this.state.displayMode,
      this.state.bubbleOpacity,
      this.state.topLabelMode
    ].join(':');

    return (
      <div className="heatmap-shell">
        <Row className="pref-workspace-row align-items-start mb-4">
          <Col xl="8" lg="12">
            <Card className="pref-workspace-card heatmap-map-card">
              <div className="pref-workspace-head">
                <div>
                  <div className="pref-workspace-kicker">Spatial Analysis</div>
                  <div className="pref-workspace-title">{scope.title}</div>
                  <p className="pref-workspace-copy mb-0">{this.getModeSummaryCopy()}</p>
                </div>
                <span className="pref-workspace-badge">{(this.state.displayMode === 'bubble' ? 'Bubble' : 'Filled') + ' · ' + this.getModeLabel() + ' view'}</span>
              </div>

              <div className="heatmap-toolbar heatmap-toolbar-inline">
                {this.renderMetricPicker()}
                <div className="heatmap-toolbar-field heatmap-toolbar-year">
                  <label className="heatmap-label">Year</label>
                  <Input type="select" value={selectedYear} onChange={(event) => this.setState({ selectedYear: event.target.value })}>
                    {(heatmapData && heatmapData.available_years ? heatmapData.available_years : []).map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </Input>
                </div>
                <div className="heatmap-toolbar-field heatmap-toolbar-history">
                  <HistoryControls
                    historyWindow={this.state.historyWindow}
                    onChange={(historyWindow) => this.setState({ historyWindow })}
                    availableYears={heatmapData && heatmapData.available_years ? heatmapData.available_years.length : 0} />
                </div>
                {this.renderDisplayToggle()}
                {this.renderBubbleOpacityControl()}
                {this.renderTopLabelToggle()}
                {this.renderModeToggle()}
                {(this.props.province || this.props.prefecture) ? (
                  <Button className="sl-inline-action" onClick={() => this.handleBackScope()}>
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    {this.props.prefecture ? 'Back to prefectures' : 'Back to provinces'}
                  </Button>
                ) : null}
              </div>

              <div className="heatmap-map-shell" ref={this.containerRef} onMouseLeave={() => this.setState({ hoveredFeatureId: null, bubbleTooltip: null })}>
                {loading ? (
                  <div className="heatmap-loading">
                    <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary" />
                  </div>
                ) : (
                  <ComposableMap
                    key={mapRenderKey}
                    projection={(width, height) => this.buildProjection(width, height)}
                    width={mapWidth}
                    height={mapHeight}
                    style={{ width: '100%', height: '100%' }}>
                    <Geographies geography={geographies}>
                      {(loadedGeographies, projection) => {
                        const pathGenerator = geoPath().projection(projection);
                        const topBubbleIds = this.getTopBubbleIds();
                        return (
                          <>
                            {loadedGeographies.map((geography, index) => {
                              const matchedFeature = this.getFeatureForGeo(geography.properties);
                              const isActive = matchedFeature && (this.state.hoveredFeatureId || this.state.selectedFeatureId) === matchedFeature.id;
                              const geographyKey = geography.id || geography.properties.id || geography.properties.name || index;
                              const polygonFill = this.state.displayMode === 'filled' ? this.getBubbleColor(matchedFeature) : this.getBaseMapFill();
                              return (
                                <Geography
                                  key={geographyKey}
                                  geography={geography}
                                  projection={projection}
                                  onClick={() => this.handleGeoClick(geography.properties)}
                                  onMouseEnter={() => this.setState({ hoveredFeatureId: matchedFeature ? matchedFeature.id : null })}
                                  onMouseMove={() => this.setState({ hoveredFeatureId: matchedFeature ? matchedFeature.id : null })}
                                  onMouseLeave={() => this.setState({ hoveredFeatureId: null })}
                                  style={{
                                    default: {
                                      fill: polygonFill,
                                      stroke: isActive ? '#0f3f7c' : '#244567',
                                      strokeWidth: isActive ? 1.5 : 0.75,
                                      outline: 'none'
                                    },
                                    hover: {
                                      fill: polygonFill,
                                      stroke: '#0f3f7c',
                                      strokeWidth: 1.5,
                                      outline: 'none'
                                    },
                                    pressed: {
                                      fill: polygonFill,
                                      stroke: '#0f3f7c',
                                      strokeWidth: 1.5,
                                      outline: 'none'
                                    }
                                  }} />
                              );
                            })}
                            {this.state.displayMode === 'bubble' ? (
                              <g className="heatmap-bubble-layer">
                                {loadedGeographies.map((geography, index) => {
                                  const populationFeature = this.getPopulationFeatureForGeo(geography.properties);
                                  const metricFeature = this.getFeatureForGeo(geography.properties);
                                  if(!populationFeature || !populationFeature.value) return null;

                                  const [cx, cy] = pathGenerator.centroid(geography);
                                  if(!Number.isFinite(cx) || !Number.isFinite(cy)) return null;

                                  const areaCap = Math.sqrt(Math.max(pathGenerator.area(geography), 1) / Math.PI);
                                  const radius = this.getBubbleRadius(populationFeature, areaCap);
                                  if(radius <= 0) return null;

                                  const bubbleColor = this.getBubbleColor(metricFeature);
                                  const bubbleOpacity = this.state.bubbleOpacity / 100;
                                  const bubbleGlow = this.hexToRgba(bubbleColor, Math.max(0.08, bubbleOpacity * 0.2));
                                  const bubbleStroke = this.hexToRgba(bubbleColor, Math.max(0.3, bubbleOpacity * 0.72));
                                  const bubbleCore = this.hexToRgba(bubbleColor, Math.max(0.2, bubbleOpacity * 0.88));
                                  const labelY = Math.max(16, cy - radius - 6);
                                  const labelFeature = metricFeature || populationFeature;
                                  const showLabel = topBubbleIds.includes(populationFeature.id);
                                  const showTooltip = showLabel && this.state.topLabelMode === 'tooltip';
                                  const showTextLabel = showLabel && this.state.topLabelMode === 'labels';

                                  return (
                                    <g key={'bubble-' + (geography.id || geography.properties.id || geography.properties.name || index)}>
                                      <circle className="heatmap-bubble-glow" cx={cx} cy={cy} r={radius} style={{ fill: bubbleGlow, stroke: bubbleStroke }} />
                                      <circle className="heatmap-bubble-core" cx={cx} cy={cy} r={Math.max(radius - 2, 2)} style={{ fill: bubbleCore, stroke: this.hexToRgba(bubbleColor, 0.92) }} />
                                      {showTextLabel ? (
                                        <text
                                          className="heatmap-bubble-label"
                                          x={cx}
                                          y={labelY}
                                          style={{ fill: this.hexToRgba(bubbleColor, 0.98) }}>
                                          {labelFeature.name}
                                        </text>
                                      ) : null}
                                      {showTooltip ? (
                                        <circle
                                          className="heatmap-bubble-hit"
                                          cx={cx}
                                          cy={cy}
                                          r={radius + 10}
                                          onMouseEnter={() => this.setState({
                                            hoveredFeatureId: labelFeature.id,
                                            bubbleTooltip: {
                                              x: cx,
                                              y: Math.max(20, cy - radius - 14),
                                              name: labelFeature.name,
                                              metricValue: this.getModeFormattedValue(labelFeature),
                                              populationValue: this.formatPopulationValue(populationFeature.value)
                                            }
                                          })}
                                          onMouseMove={() => this.setState({
                                            hoveredFeatureId: labelFeature.id,
                                            bubbleTooltip: {
                                              x: cx,
                                              y: Math.max(20, cy - radius - 14),
                                              name: labelFeature.name,
                                              metricValue: this.getModeFormattedValue(labelFeature),
                                              populationValue: this.formatPopulationValue(populationFeature.value)
                                            }
                                          })}
                                          onMouseLeave={() => this.setState({ bubbleTooltip: null, hoveredFeatureId: null })}
                                          onClick={() => this.handleGeoClick(geography.properties)} />
                                      ) : null}
                                    </g>
                                  );
                                })}
                              </g>
                            ) : null}
                          </>
                        );
                      }}
                    </Geographies>
                  </ComposableMap>
                )}
                {this.renderBubbleTooltip()}
              </div>

              <div className="heatmap-map-meta">
                {this.renderInlineReadout()}
                {this.renderLegendInline()}
              </div>

              <div className="pref-workspace-footer heatmap-footer">
                <div className="pref-workspace-stat">
                  <div className="pref-workspace-stat-label">Level</div>
                  <div className="pref-workspace-stat-value">{scope.level}</div>
                  <div className="pref-workspace-stat-note">{scope.subtitle}</div>
                </div>
                <div className="pref-workspace-stat">
                  <div className="pref-workspace-stat-label">Metric</div>
                  <div className="pref-workspace-stat-value">{this.state.selectedMetric || '—'}</div>
                  <div className="pref-workspace-stat-note">{this.state.historyWindow === 'recent' ? 'Recent window' : this.state.historyWindow === '5y' ? '5-year window' : this.state.historyWindow === '10y' ? '10-year window' : 'Full history window'}</div>
                </div>
                <div className="pref-workspace-stat">
                  <div className="pref-workspace-stat-label">Coverage</div>
                  <div className="pref-workspace-stat-value">{summary ? summary.count : 0} regions</div>
                  <div className="pref-workspace-stat-note">Colored boundary coverage</div>
                </div>
                <div className="pref-workspace-stat">
                  <div className="pref-workspace-stat-label">Distribution</div>
                  <div className="pref-workspace-stat-value">{summary ? summary.median.toLocaleString() : '—'}</div>
                  <div className="pref-workspace-stat-note">Median for current scope</div>
                </div>
              </div>
            </Card>
          </Col>

          <Col xl="4" lg="12">
            <div className="pref-analytics-shell heatmap-side-shell heatmap-side-shell-compact">
              <div className="pref-kpi-grid heatmap-side-kpis mt-0 heatmap-side-kpis-centered">
                <div className="pref-kpi-card pref-kpi-card-detailed">
                  <div className="pref-kpi-label">Minimum</div>
                  <div className="pref-kpi-value">{summary ? summary.min.toLocaleString() : '—'}</div>
                  <div className="pref-kpi-sub">Lowest mapped score</div>
                </div>
                <div className="pref-kpi-card pref-kpi-card-detailed">
                  <div className="pref-kpi-label">Median</div>
                  <div className="pref-kpi-value">{summary ? summary.median.toLocaleString() : '—'}</div>
                  <div className="pref-kpi-sub">Middle of current scope</div>
                </div>
                <div className="pref-kpi-card pref-kpi-card-detailed">
                  <div className="pref-kpi-label">Mean</div>
                  <div className="pref-kpi-value">{summary ? summary.mean.toLocaleString() : '—'}</div>
                  <div className="pref-kpi-sub">Average across mapped regions</div>
                </div>
                <div className="pref-kpi-card pref-kpi-card-detailed">
                  <div className="pref-kpi-label">Maximum</div>
                  <div className="pref-kpi-value">{summary ? summary.max.toLocaleString() : '—'}</div>
                  <div className="pref-kpi-sub">Highest mapped score</div>
                </div>
              </div>

              <Card className="heatmap-side-card">
                <CardBody>
                  <div className="heatmap-side-title"><FontAwesomeIcon icon={faProjectDiagram} /> Analysis Mode</div>
                  <div className="heatmap-summary-line">Mode: <strong>{this.getModeLabel()}</strong></div>
                  <div className="heatmap-summary-line">Current year: <strong>{heatmapData && heatmapData.selected_year ? heatmapData.selected_year : '—'}</strong></div>
                  <div className="heatmap-summary-line">Mapped regions: <strong>{summary ? summary.count : 0}</strong></div>
                  <div className="heatmap-summary-line">Metric unit: <strong>{heatmapData && heatmapData.unit ? heatmapData.unit : 'Raw value'}</strong></div>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>

        <Row className="heatmap-detail-grid g-4 justify-content-center">
          <Col xl="5" lg="6">{this.renderSelectionPanel()}</Col>
          <Col xl="3" lg="6">{topItems.length > 0 ? this.renderRankedList('Leading regions', topItems) : null}</Col>
          <Col xl="4" lg="12">{bottomItems.length > 0 ? this.renderRankedList('Trailing regions', bottomItems) : null}</Col>
        </Row>
      </div>
    );
  }
}

export default HeatmapExplorer;
